import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, dateFmt, errMsg, LEAD_CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import StatusBadge from "@/components/StatusBadge";
import GmRateRequests from "@/components/GmRateRequests";
import SpaceTracker from "@/components/SpaceTracker";
import EventsCalendar from "@/components/EventsCalendar";
import FunctionDiary from "@/components/FunctionDiary";
import AccountsPanel from "@/components/AccountsPanel";
import BookingsBoard from "@/components/BookingsBoard";
import SalesReport from "@/components/SalesReport";
import MyProperty from "@/components/MyProperty";
import {
  Target, Trophy, ListChecks, Users, UserPlus, BellRinging, PlusCircle, Tag, MagnifyingGlass, PhoneCall,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="p-6 bg-white" data-testid={`gm-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon size={26} weight="duotone" color={accent || "#002FA7"} />
      <div className="font-display font-black text-3xl text-[#111827] mt-3">{value}</div>
      <div className="text-sm text-[#4B5563] mt-1">{label}</div>
    </div>
  );
}

function AddSalesDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", title: "Sales Manager" });
  const mut = useMutation({
    mutationFn: (body) => api.createSales(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["sales"] }); toast.success("Sales user created — they can log in now"); setOpen(false); setF({ name: "", email: "", password: "", title: "Sales Manager" }); },
    onError: (e) => toast.error(errMsg(e, "Could not create sales user")),
  });
  const F = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-sales-btn" className="inline-flex items-center gap-2 bg-[#111827] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#002FA7] transition-colors active:scale-[0.98]">
          <UserPlus size={16} weight="bold" /> Add sales user
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#111827] max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">New Sales Manager</DialogTitle></DialogHeader>
        <p className="text-sm text-[#4B5563] -mt-2">They get a login and can work leads you assign.</p>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Name</span>
          <input data-testid="sales-name" value={f.name} onChange={F("name")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Title</span>
          <input data-testid="sales-title" value={f.title} onChange={F("title")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Login email</span>
          <input data-testid="sales-email" type="email" value={f.email} onChange={F("email")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Temp password</span>
          <input data-testid="sales-password" value={f.password} onChange={F("password")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <DialogFooter>
          <button data-testid="save-sales-btn" disabled={mut.isPending}
            onClick={() => { if (!f.name.trim() || !f.email.trim() || f.password.length < 6) return toast.error("Name, email and a 6+ char password required"); mut.mutate(f); }}
            className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60">
            {mut.isPending ? "Creating…" : "Create sales user"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddLeadDialog({ hotels }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const blank = { company_name: "", category: "Corporate/Crew", industry: "", city: "", email: "", phone: "", address: "", website: "", hotel_id: "" };
  const [f, setF] = useState(blank);
  const mut = useMutation({
    mutationFn: (body) => api.createLead(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["leads"] }); toast.success("Lead added"); setOpen(false); setF(blank); },
    onError: (e) => toast.error(errMsg(e, "Could not add lead")),
  });
  const F = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-lead-btn" className="inline-flex items-center gap-2 bg-[#111827] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#002FA7] transition-colors active:scale-[0.98]">
          <PlusCircle size={16} weight="bold" /> Add lead manually
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#111827] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add a Lead</DialogTitle></DialogHeader>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Company name</span>
          <input data-testid="lead-company" value={f.company_name} onChange={F("company_name")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Business type</span>
            <select data-testid="lead-category" value={f.category} onChange={F("category")} className="w-full border border-[#E5E7EB] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              {LEAD_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Industry</span>
            <input data-testid="lead-industry" value={f.industry} onChange={F("industry")} placeholder="e.g. Energy" className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">City</span>
            <input data-testid="lead-city" value={f.city} onChange={F("city")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Phone</span>
            <input data-testid="lead-phone" value={f.phone} onChange={F("phone")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        </div>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Email</span>
          <input data-testid="lead-email" value={f.email} onChange={F("email")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Associated property (optional)</span>
          <select data-testid="lead-hotel" value={f.hotel_id} onChange={F("hotel_id")} className="w-full border border-[#E5E7EB] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
            <option value="">— None —</option>
            {hotels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select></label>
        <DialogFooter>
          <button data-testid="save-lead-btn" disabled={mut.isPending}
            onClick={() => { if (!f.company_name.trim()) return toast.error("Company name is required"); mut.mutate(f); }}
            className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60">
            {mut.isPending ? "Adding…" : "Add lead"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddFrontDeskDialog({ hotels }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const blank = { name: "Front Desk", email: "", password: "", role: "front_desk", title: "Front Desk", property_ids: [] };
  const [f, setF] = useState(blank);
  const F = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const mut = useMutation({
    mutationFn: (body) => api.createTeamMember(body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["fdAccounts"] }); toast.success("Front desk login created"); setOpen(false); setF(blank); },
    onError: (e) => toast.error(errMsg(e, "Could not create front desk login")),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-frontdesk-btn" className="inline-flex items-center gap-2 bg-[#0891B2] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#0E7490] transition-colors active:scale-[0.98]">
          <UserPlus size={16} weight="bold" /> Add front desk login
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#111827] max-w-md">
        <DialogHeader><DialogTitle className="font-display text-2xl">New Front Desk login</DialogTitle></DialogHeader>
        <p className="text-sm text-[#4B5563] -mt-2">A shared login for one property to log guest enquiries.</p>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Property</span>
          <select data-testid="fd-property" value={f.property_ids[0] || ""} onChange={(e) => setF((s) => ({ ...s, property_ids: e.target.value ? [e.target.value] : [] }))}
            className="w-full border border-[#E5E7EB] px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
            <option value="">Select a property…</option>
            {hotels.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Display name</span>
          <input data-testid="fd-name" value={f.name} onChange={F("name")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Login email</span>
          <input data-testid="fd-email" type="email" value={f.email} onChange={F("email")} placeholder="frontdesk@property.com" className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Temp password</span>
          <input data-testid="fd-password" value={f.password} onChange={F("password")} className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        <DialogFooter>
          <button data-testid="save-frontdesk-btn" disabled={mut.isPending}
            onClick={() => { if (f.property_ids.length === 0) return toast.error("Pick a property"); if (!f.email.trim() || f.password.length < 6) return toast.error("Email and a 6+ char password required"); mut.mutate(f); }}
            className="bg-[#0891B2] text-white px-6 py-3 font-semibold hover:bg-[#0E7490] transition-colors active:scale-[0.98] disabled:opacity-60">
            {mut.isPending ? "Creating…" : "Create login"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "property", label: "Property" },
  { id: "leads", label: "Leads" },
  { id: "requests", label: "Rate Requests" },
  { id: "space", label: "Rooms & Space" },
  { id: "calendar", label: "Calendar" },
  { id: "diary", label: "Function Diary" },
  { id: "accounts", label: "Accounts" },
  { id: "bookings", label: "Bookings" },
  { id: "reports", label: "Sales Report" },
  { id: "frontdesk", label: "Front Desk" },
  { id: "sales", label: "Sales Team" },
];

const ROLE_TITLE = { gm: "General Manager", rm: "Regional Manager", dos: "Director of Sales" };

export default function GmDashboard() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (t) => setParams({ tab: t });

  const { data: stats } = useQuery({ queryKey: ["leadStats"], queryFn: api.leadStats });
  const { data: leads = [] } = useQuery({ queryKey: ["leads", "gm"], queryFn: () => api.leads() });
  const { data: sales = [] } = useQuery({ queryKey: ["sales"], queryFn: api.listSales });
  const { data: upcoming } = useQuery({ queryKey: ["upcomingReminders"], queryFn: api.upcomingReminders });
  const { data: hotels = [] } = useQuery({ queryKey: ["myProperties"], queryFn: api.myProperties });
  const canFrontDesk = user.role === "gm" || user.role === "rm";
  const { data: fdAccounts = [] } = useQuery({ queryKey: ["fdAccounts"], queryFn: api.frontDeskAccounts, enabled: canFrontDesk });
  const visibleTabs = TABS.filter((t) => t.id !== "frontdesk" || canFrontDesk);

  const [leadQ, setLeadQ] = useState("");
  const [leadCat, setLeadCat] = useState("all");
  const [leadAssignee, setLeadAssignee] = useState("all");
  const leadCities = Array.from(new Set(leads.map((l) => l.city).filter(Boolean))).sort();
  const [leadCity, setLeadCity] = useState("all");
  const assigneeOptions = Array.from(
    leads.reduce((m, l) => { if (l.assigned_to) m.set(l.assigned_to, l.assigned_to_name || "Assigned"); return m; }, new Map())
  ).map(([id, name]) => ({ id, name }));
  const filteredLeads = leads.filter((l) => {
    if (leadCat !== "all" && l.category !== leadCat) return false;
    if (leadCity !== "all" && l.city !== leadCity) return false;
    if (leadAssignee === "mine" && l.assigned_to !== user.id) return false;
    if (leadAssignee === "unassigned" && l.assigned_to) return false;
    if (leadAssignee !== "all" && leadAssignee !== "mine" && leadAssignee !== "unassigned" && l.assigned_to !== leadAssignee) return false;
    if (leadQ) {
      const t = `${l.company_name} ${l.industry} ${l.email} ${l.phone} ${l.city} ${l.address}`.toLowerCase();
      if (!t.includes(leadQ.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="overline text-[#002FA7]">{ROLE_TITLE[user.role] || "Manager"} workspace</span>
          <h1 className="font-display font-black text-4xl text-[#111827] mt-2">Hi {user.name}</h1>
          <p className="text-[#4B5563] mt-1">{user.company_name} · {hotels.length} propert{hotels.length === 1 ? "y" : "ies"} · manage prospects, leads & assign to sales</p>
        </div>
        <Link to="/prospect" data-testid="gm-prospect-cta"
          className="inline-flex items-center gap-2 bg-[#002FA7] text-white px-5 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98]">
          <Target size={18} weight="bold" /> Find prospects
        </Link>
      </div>

      <div className="flex gap-1 border-b border-[#E5E7EB] mt-8 overflow-x-auto">
        {visibleTabs.map((t) => (
          <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition-colors ${tab === t.id ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-[#4B5563] hover:text-[#111827]"}`}>
            {t.label}{t.id === "leads" && leads.length > 0 ? ` (${leads.length})` : ""}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="mt-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 grid-skeleton border-l border-t border-[#E5E7EB]">
            <Stat icon={ListChecks} label="Total Leads" value={stats?.total ?? 0} />
            <Stat icon={Target} label="Unassigned" value={stats?.unassigned ?? 0} accent="#D97706" />
            <Stat icon={Users} label="Sales Team" value={sales.length} />
            <Stat icon={Trophy} label="Won" value={stats?.won ?? 0} accent="#047857" />
          </div>
          <div className="border border-[#111827] p-6 mt-6" data-testid="gm-upcoming-reminders">
            <div className="flex items-center gap-2">
              <BellRinging size={18} weight="bold" color="#B45309" />
              <span className="font-semibold text-[#111827]">Upcoming reminders & repeat business</span>
            </div>
            <div className="mt-4 space-y-2">
              {(!upcoming || upcoming.count === 0) && <p className="text-sm text-[#9CA3AF]">No reminders set. Open a lead to schedule a return date.</p>}
              {upcoming?.reminders?.slice(0, 8).map((r) => (
                <Link key={r.reminder_id} to={`/leads/${r.lead_id}`}
                  className="flex items-center justify-between border border-[#E5E7EB] px-3 py-2 text-sm hover:border-[#002FA7] transition-colors">
                  <span className="text-[#111827]"><b>{r.company_name}</b> — {r.note}</span>
                  <span className="font-mono text-[#B45309] font-semibold">{dateFmt(r.date)}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "leads" && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <span className="text-sm text-[#4B5563]">{filteredLeads.length} of {leads.length} leads</span>
            <AddLeadDialog hotels={hotels} />
          </div>
          <div className="grid md:grid-cols-12 gap-3 mb-5 bg-white border border-[#E5E7EB] p-3">
            <div className="md:col-span-5 relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input data-testid="lead-search" value={leadQ} onChange={(e) => setLeadQ(e.target.value)}
                placeholder="Search leads by name, industry, email, phone, city…"
                className="w-full pl-9 pr-3 py-2 border border-[#E5E7EB] text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            </div>
            <select data-testid="lead-filter-assignee" value={leadAssignee} onChange={(e) => setLeadAssignee(e.target.value)}
              className="md:col-span-3 border border-[#E5E7EB] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              <option value="all">Everyone's leads</option>
              <option value="mine">My leads</option>
              <option value="unassigned">Unassigned</option>
              {assigneeOptions.filter((o) => o.id !== user.id).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
            <select data-testid="lead-filter-category" value={leadCat} onChange={(e) => setLeadCat(e.target.value)}
              className="md:col-span-2 border border-[#E5E7EB] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              <option value="all">All types</option>
              {LEAD_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="lead-filter-city" value={leadCity} onChange={(e) => setLeadCity(e.target.value)}
              className="md:col-span-2 border border-[#E5E7EB] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
              <option value="all">All locations</option>
              {leadCities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="space-y-3" data-testid="gm-leads-list">
            {leads.length === 0 && <div className="border border-dashed border-[#E5E7EB] py-16 text-center text-[#4B5563]">No leads yet. Use "Find prospects" or "Add lead manually".</div>}
            {leads.length > 0 && filteredLeads.length === 0 && <div className="border border-dashed border-[#E5E7EB] py-16 text-center text-[#4B5563]">No leads match your filters.</div>}
            {filteredLeads.map((l) => (
              <Link key={l.id} to={`/leads/${l.id}`} data-testid={`gm-lead-${l.id}`} className="rb-card rb-card-hover flex items-center justify-between p-5">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-lg text-[#111827]">{l.company_name}</span>
                    {l.source === "Front Desk" && <span className="inline-flex items-center gap-1 bg-[#CFFAFE] text-[#0E7490] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">Front Desk</span>}
                    {l.category && <span className="inline-flex items-center gap-1 bg-[#EDE9FE] text-[#6D28D9] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"><Tag size={10} weight="fill" /> {l.category}</span>}
                    {(l.reminders || []).some((r) => !r.done) && <BellRinging size={14} weight="fill" color="#B45309" />}
                  </div>
                  <div className="text-sm text-[#4B5563]">{l.industry || "Prospect"} · {l.city || "—"} · {l.phone || l.email || "no contact"}</div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-[#4B5563] hidden sm:block">{l.assigned_to_name ? `→ ${l.assigned_to_name}` : "Unassigned"}</span>
                  <StatusBadge status={l.status} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {tab === "requests" && <GmRateRequests />}
      {tab === "property" && <MyProperty hotels={hotels} />}
      {tab === "space" && <SpaceTracker />}
      {tab === "calendar" && <EventsCalendar />}
      {tab === "diary" && <FunctionDiary />}
      {tab === "accounts" && <AccountsPanel />}
      {tab === "bookings" && <BookingsBoard />}
      {tab === "reports" && <SalesReport />}

      {tab === "frontdesk" && canFrontDesk && (
        <div className="mt-8" data-testid="frontdesk-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2"><PhoneCall size={20} weight="duotone" color="#0891B2" /><span className="font-semibold text-[#111827]">Front desk logins</span></div>
              <p className="text-sm text-[#4B5563] mt-1">Create a shared login per property so the front desk can log guest enquiries straight to your team.</p>
            </div>
            <AddFrontDeskDialog hotels={hotels} />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="frontdesk-grid">
            {fdAccounts.length === 0 && <div className="border border-dashed border-[#E5E7EB] py-16 text-center text-[#4B5563] col-span-full">No front desk logins yet.</div>}
            {fdAccounts.map((fd) => {
              const propName = hotels.find((h) => (fd.property_ids || []).includes(h.id))?.name;
              return (
                <div key={fd.id} className="rb-card p-5" data-testid={`frontdesk-user-${fd.id}`}>
                  <div className="w-10 h-10 bg-[#0891B2] text-white flex items-center justify-center font-display font-bold"><PhoneCall size={18} weight="fill" /></div>
                  <div className="font-display font-bold text-lg text-[#111827] mt-3">{fd.name}</div>
                  <div className="text-sm text-[#4B5563]">{propName || "Property"}</div>
                  <div className="text-xs text-[#9CA3AF] mt-1">{fd.email}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "sales" && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><Users size={20} weight="duotone" color="#002FA7" /><span className="font-semibold text-[#111827]">Your sales team</span></div>
            <span className="text-xs text-[#9CA3AF]">Managed by your Group Admin</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sales-team-grid">
            {sales.length === 0 && <div className="border border-dashed border-[#E5E7EB] py-16 text-center text-[#4B5563] col-span-full">No sales users yet. Add one so you can assign leads.</div>}
            {sales.map((s) => {
              const assigned = leads.filter((l) => l.assigned_to === s.id);
              return (
                <div key={s.id} className="rb-card p-5" data-testid={`sales-user-${s.id}`}>
                  <div className="w-10 h-10 bg-[#111827] text-white flex items-center justify-center font-display font-bold">{s.name[0]}</div>
                  <div className="font-display font-bold text-lg text-[#111827] mt-3">{s.name}</div>
                  <div className="text-sm text-[#4B5563]">{s.title}</div>
                  <div className="text-xs text-[#9CA3AF] mt-1">{s.email}</div>
                  <div className="mt-3 bg-[#E0E7FF] text-[#002FA7] px-3 py-1.5 text-sm font-semibold inline-block">{assigned.length} leads assigned</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
